/* -------------------------------------------------------------------------------------------------
 * ISOMORPHIC (should work in both client and server)
 * -----------------------------------------------------------------------------------------------*/

import {
  DB,
  Entry,
  findEntry,
  getFindEntriesQuery,
  getNestedBlocksSQL,
  getSpaceLineage,
} from '@basehub/data/db/index'
import {
  type Block,
  type Branch,
  type Repository,
  type RootBlock,
  type Team,
  getTeamSpaceName,
  getRepoSpaceName,
  BlockRelationship,
  CommonBRsCompoundIndexes,
  commonBRsCompoundIndexes,
  compareOrder,
  isBRInRoot,
} from '@basehub/data/replicache/index'
import { getTreeManager } from '@basehub/utils/tree/index'
import { blockUsageSchema } from '@basehub/data/generic-redis/index'
import { getAndPopulateCache } from '@basehub/data/api-kv/index'
import { getBlockUsageFromTreeManager } from './blocks-usage'
import { Redis } from '@upstash/redis'
import { z } from 'zod'
import { invariant } from '@epic-web/invariant'
import { IndexedMap, createIndexedMap } from '@basehub/utils/indexed-map/index'

const getBlockUsageFromRootBlock = (
  rootBlock: RootBlock,
  {
    blocksMap,
    blockRelationshipsMap,
  }: {
    blocksMap: IndexedMap<Block>
    blockRelationshipsMap: IndexedMap<
      BlockRelationship,
      CommonBRsCompoundIndexes
    >
  }
) => {
  const treeManager = getTreeManager<Block, NonNullable<unknown>>({
    getKey: (item) => item.id,
    getOrder: (key) => {
      const br = blockRelationshipsMap.get(key)
      invariant(br, `block relationship for ${key} not found`)
      return br.order
    },
    getChildrenKeys: (blockId) => {
      const childBRs = blockRelationshipsMap.compoundIndexes
        .get('byParentId')
        ?.get(blockId)
      if (!childBRs) return []
      return Array.from(childBRs)
        .map((brId) => {
          const br = blockRelationshipsMap.get(brId)
          invariant(br, `block relationship ${brId} not found`)
          if (!isBRInRoot(br, rootBlock)) return null
          return br
        })
        .filter(Boolean)
        .sort(compareOrder)
        .map((br) => br.blockId)
    },
    rootKey: rootBlock.id,
    getValue: (key) => blocksMap.get(key)!,
    indexKeys: ['sourceBlockId'],
    autoFallbackWithKeys: ['sourceBlockId'],
  })

  return getBlockUsageFromTreeManager(treeManager)
}

export const blockUsageFromTeamSchema = z.object({
  total: z.number(),
  byRepoIds: z.record(z.number()),
})

export type BlockUsageFromTeam = z.infer<typeof blockUsageFromTeamSchema>

export const getBlockUsageFromTeam = async (
  team: Team,
  {
    excludedRepoIds,
    db,
    redis,
    revalidateCache,
  }: {
    excludedRepoIds?: Array<Repository['id']>
    db: DB
    redis: Redis
    revalidateCache?: boolean
  }
): Promise<BlockUsageFromTeam> => {
  return await getAndPopulateCache({
    get: async () => await blockUsageSchema.get(redis, team.id),
    fallbackGet: async () => {
      const repositories = (
        (await getFindEntriesQuery({
          db,
          entryType: 'repository',
          spaceID: getTeamSpaceName(team.id),
        })) as Array<Entry<Repository>>
      ).filter((r) => !excludedRepoIds?.includes(r.value.id))

      if (!repositories.length) {
        return {
          total: 0,
          byRepoIds: {},
        }
      }

      // 1. iterate over all repositories and retrieve the root block & block list from default branch
      const teamCrucialBlocksPerRepo = await Promise.all(
        repositories.map(async (repo) => {
          return await getRootBlockAndBlocksFromRepo(repo.value, {
            teamId: team.id,
            db,
          })
        })
      )

      const byRepoIds: Record<string, number> = {}
      let total: number = 0

      // 2. count blocks
      teamCrucialBlocksPerRepo.forEach(
        ({ rootBlock, blocksMap, blockRelationshipsMap, repoId }) => {
          if (!rootBlock) return
          const treeBlockUsage = getBlockUsageFromRootBlock(rootBlock, {
            blocksMap,
            blockRelationshipsMap,
          })
          total += treeBlockUsage
          byRepoIds[repoId] = treeBlockUsage
        }
      )

      return { total, byRepoIds }
    },
    set: async (data) => await blockUsageSchema.set(redis, team.id, data),
    throwOnNotFound: true,
    revalidate: revalidateCache,
  })
}

const getRootBlockAndBlocksFromRepo = async (
  repository: Repository,
  { teamId, db }: { teamId: string; db: DB }
) => {
  // 1. retrieve default branch from repo
  const repoSpaceId = getRepoSpaceName(teamId, repository.id)

  const [mainBranch, spaceLineage] = await Promise.all([
    findEntry<Branch>({
      db,
      entryType: 'branch',
      spaceID: repoSpaceId,
      valueFilter: { path: 'isDefault', equals: true },
    }),
    getSpaceLineage({
      db,
      spaceID: repoSpaceId,
    }),
  ])

  const rootBlockId = mainBranch?.value.headCommit?.rootBlockId

  if (!rootBlockId) {
    return { rootBlock: null, allBlocks: null }
  }

  const entriesInTree = await db.execute<Entry<Block | BlockRelationship>>(
    getNestedBlocksSQL({
      cvrKey: null,
      cvrSyncSequence: null,
      rootBlockId,
      spaceLineage,
    })
  )

  let rootBlock
  const blocksMap = createIndexedMap<Block>({
    autoFallbackKeys: ['sourceBlockId'],
  })
  const blockRelationshipsMap = createIndexedMap<
    BlockRelationship,
    CommonBRsCompoundIndexes
  >({
    autoFallbackKeys: ['sourceBlockId', 'blockId'],
    compoundIndexes: commonBRsCompoundIndexes,
  })
  entriesInTree.forEach((e) => {
    if (e.key.startsWith('block/')) {
      const block = e.value as Block
      if (block.type === 'root') {
        rootBlock = block
      }
      blocksMap.set(block.id, block)
    } else if (e.key.startsWith('br/')) {
      blockRelationshipsMap.set(e.value.id, e.value as BlockRelationship)
    }
  })

  return { rootBlock, blocksMap, blockRelationshipsMap, repoId: repository.id }
}
